import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ReplaySubject, Subject, finalize, takeUntil } from 'rxjs';
import { RapportParcoursFormationService } from 'src/services/rapport-parcours-formation.service';
import { RapportParcoursFormationLigneModel, RapportParcoursFormationStatsModel } from 'src/models/rapport/rapport-parcours-formation.model';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { CustomPaginatorIntlService } from 'src/services/custom-paginator-intl.service';
import { StringHelpers } from 'src/models/helpers/string-helpers';

@Component({
    selector: 'app-vue-parcours',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        MatSelectModule,
        MatButtonModule,
        MatMenuModule,
        MatTooltipModule,
        NgxMatSelectSearchModule
    ],
    templateUrl: './vue-parcours.component.html',
    styleUrl: './vue-parcours.component.scss',
    providers: [{ provide: MatPaginatorIntl, useClass: CustomPaginatorIntlService }]
})
export class VueParcoursComponent implements OnInit, OnDestroy {
  @Output() voirDetails = new EventEmitter<number>();
  @ViewChild(MatSort) set matSort(sort: MatSort | undefined) {
    if (!sort) return;
    this.dataSource.sort = sort;
  }

  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator | undefined) {
    if (!paginator) return;
    this.dataSource.paginator = paginator;
  }

  stats?: RapportParcoursFormationStatsModel;
  dataSource = new MatTableDataSource<RapportParcoursFormationLigneModel>([]);
  displayedColumns = ['nom', 'nbreContenus', 'nbreUtilisateursVises', 'pourcentageComplete', 'pourcentageAvancement', 'actions'];

  isLoading = false;
  recherche = '';
  filtreOrganisation: string[] = [];
  organisations: string[] = [];
  filteredOrganisations: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  organisationFilterCtrl: FormControl = new FormControl();
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  private _onDestroy = new Subject<void>();

  constructor(
    private rapportService: RapportParcoursFormationService,
    private utilisateurService: UtilisateurService
  ) {}

  ngOnInit(): void {
    this.configurerDataSource();
    this.organisationFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => this.filtrerOrganisations());
    this.charger();
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private configurerDataSource(): void {
    this.dataSource.filterPredicate = (row, filter) => {
      const criteres = JSON.parse(filter || '{}') as { recherche?: string; organisations?: string[] };
      const recherche = StringHelpers.removeDiacritics(criteres.recherche ?? '');
      const nomParcours = StringHelpers.removeDiacritics(row.nom);
      const nomOrganisation = StringHelpers.removeDiacritics(row.nomOrganisation ?? '');
      const organisationsFiltre = criteres.organisations ?? [];

      return (!recherche || nomParcours.includes(recherche) || nomOrganisation.includes(recherche))
        && (organisationsFiltre.length === 0 || organisationsFiltre.includes(row.nomOrganisation));
    };
  }

  charger(): void {
    this.isLoading = true;
    this.rapportService.obtenirRapportParcours()
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(reponse => {
        this.stats = reponse.stats;
        this.dataSource.data = reponse.lignes;
        this.organisations = this.obtenirOptions(reponse.lignes.map(ligne => ligne.nomOrganisation));
        this.filteredOrganisations.next(this.organisations.slice());
        this.appliquerFiltres();
      });
  }

  private filtrerOrganisations(): void {
    if (!this.organisations) return;
    let search = (this.organisationFilterCtrl.value ?? '').toString();
    if (!search) {
      this.filteredOrganisations.next(this.organisations.slice());
      return;
    }
    search = search.toLowerCase();
    this.filteredOrganisations.next(
      this.organisations.filter(o => o.toLowerCase().indexOf(search) > -1)
    );
  }

  appliquerRecherche(): void {
    this.appliquerFiltres();
  }

  appliquerFiltres(): void {
    this.dataSource.filter = JSON.stringify({
      recherche: this.recherche.trim().toLowerCase(),
      organisations: this.filtreOrganisation
    });
    this.dataSource.paginator?.firstPage();
    this.recalculerStats();
  }

  private recalculerStats(): void {
    const lignes = this.dataSource.filteredData;
    const lignesAvecUtilisateurs = lignes.filter(ligne => ligne.nbreUtilisateursVises > 0);

    this.stats = {
      nombreParcours: lignes.length,
      totalFormations: lignes.reduce((total, ligne) => total + ligne.nbreContenus, 0),
      utilisateursVises: lignes.reduce((total, ligne) => total + ligne.nbreUtilisateursVises, 0),
      pourcentageCompleteMoyen: lignesAvecUtilisateurs.length
        ? Math.round(lignesAvecUtilisateurs.reduce((total, ligne) => total + ligne.pourcentageComplete, 0) / lignesAvecUtilisateurs.length)
        : 0,
      pourcentageAvancementMoyen: lignesAvecUtilisateurs.length
        ? Math.round(lignesAvecUtilisateurs.reduce((total, ligne) => total + ligne.pourcentageAvancement, 0) / lignesAvecUtilisateurs.length)
        : 0,
      utilisateursARefaire: lignes.reduce((total, ligne) => total + ligne.nbreUtilisateursARefaire, 0)
    };
  }

  reinitialiserFiltres(): void {
    this.recherche = '';
    this.filtreOrganisation = [];
    this.appliquerFiltres();
  }

  exporterExcel(): void {
    this.isLoading = true;
    this.rapportService.exporterParcours()
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(blob => this.telecharger(blob, 'rapport_parcours.xlsx'));
  }

  exporterPdf(): void {
    this.isLoading = true;
    this.rapportService.exporterParcoursPdf()
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(blob => this.telecharger(blob, 'rapport_parcours.pdf'));
  }

  private telecharger(blob: Blob, nomFichier: string): void {
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = nomFichier;
    link.click();
    window.URL.revokeObjectURL(link.href);
  }

  private obtenirOptions(valeurs: string[]): string[] {
    return [...new Set(valeurs.filter(valeur => !!valeur))].sort((a, b) => a.localeCompare(b));
  }
}
